import { QUOTE_STUDIO_DEFAULT_SCRAP_PERCENT, QUOTE_STUDIO_OPS } from '../constants/quote-studio.constant';
import { PlasticsHardwareItem } from '../models/plastics-hardware-item.model';
import { PlasticsManufacturingRatesVersion } from '../models/plastics-manufacturing-rates-version.model';
import {
    QUOTE_STUDIO_EMPTY_BREAKDOWN,
    QuoteStudioCatalogPrice,
    QuoteStudioCatalogPrices,
    QuoteStudioCostBreakdown,
    QuoteStudioCostingContext,
} from '../models/quote-studio-costing.model';
import {
    QuoteStudioCostModelVersion,
    QuoteStudioHardwareRow,
    QuoteStudioLink,
    QuoteStudioMachineKey,
    QuoteStudioOpKey,
    QuoteStudioVariation,
} from '../models/quote-studio.model';
import { VendorHardwareData } from '../models/vendor-hardware-data.model';

/*
 * Quote Studio cost math, framework-free so the browser page and the Agent API
 * compute the same number from the same recipe. Nothing here caches: cost is
 * always recipe × the rates handed in, which is what lets margin be recomputed
 * against a later rates release.
 */

/* ================================================================= rates */

export function quoteStudioMachineRate(rates: PlasticsManufacturingRatesVersion, key: QuoteStudioMachineKey): number {
    const rate = rates[key];
    return typeof rate === 'number' && isFinite(rate) ? rate : 0;
}

export function quoteStudioOpRate(rates: PlasticsManufacturingRatesVersion, key: QuoteStudioOpKey): number {
    const op = QUOTE_STUDIO_OPS.find(candidate => candidate.key === key);
    const rate = op ? rates[op.rateField] : 0;
    return typeof rate === 'number' && isFinite(rate) ? rate : 0;
}

/**
 * The resins in a shot, each with its pounds: the blend when the variation has
 * one, else its single material at the full shot weight.
 */
export function quoteStudioResins(variation: QuoteStudioVariation): { materialKey: string; pounds: number }[] {
    if (variation.resins && variation.resins.length) {
        return variation.resins.map(resin => ({ materialKey: resin.materialKey, pounds: Math.max(0, resin.pounds || 0) }));
    }
    return [{ materialKey: variation.materialKey, pounds: Math.max(0, variation.shotWeight || 0) }];
}

/** what the resin in one shot costs: each resin's pounds at its own price */
export function quoteStudioResinCost(rates: PlasticsManufacturingRatesVersion, variation: QuoteStudioVariation): number {
    return quoteStudioResins(variation).reduce((total, resin) => total + resin.pounds * quoteStudioMaterialCostPerPound(rates, resin.materialKey), 0);
}

export function quoteStudioMaterialCostPerPound(rates: PlasticsManufacturingRatesVersion, key: string): number {
    const rate = (rates.materialCategoryRates ?? []).find(candidate => candidate.key === key)?.costPerPound;
    return typeof rate === 'number' && isFinite(rate) ? rate : 0;
}

/* ================================================================= variations */

/**
 * An op counts when it has minutes and has not been switched off. Toggling a
 * line off keeps its minutes in `ops` and records the key in `disabledOps`, so
 * ticking it again restores what was typed.
 */
export function quoteStudioOpIsOn(variation: QuoteStudioVariation, key: QuoteStudioOpKey): boolean {
    return key in (variation.ops ?? {}) && !(variation.disabledOps ?? []).includes(key);
}

/** the op keys that actually cost something on this variation */
export function quoteStudioActiveOpKeys(variation: QuoteStudioVariation): QuoteStudioOpKey[] {
    return Object.keys(variation.ops ?? {})
        .map(key => key as QuoteStudioOpKey)
        .filter(key => quoteStudioOpIsOn(variation, key));
}

/** a variation's scrap percent; rows from before the field existed read as the sheet's 1.5% */
export function quoteStudioScrapPercent(variation: QuoteStudioVariation): number {
    const percent = variation.scrapPercent;
    return typeof percent === 'number' && isFinite(percent) ? Math.max(0, percent) : QUOTE_STUDIO_DEFAULT_SCRAP_PERCENT;
}

/** a variation's commission percent; none unless it says otherwise */
export function quoteStudioCommissionPercent(variation: QuoteStudioVariation): number {
    const percent = variation.commissionPercent;
    return typeof percent === 'number' && isFinite(percent) ? Math.max(0, percent) : 0;
}

/** which variation is live — everything upstream costs against it */
export function quoteStudioQuotedVariation(version: QuoteStudioCostModelVersion): QuoteStudioVariation {
    return version.variations.find(variation => variation.id === version.quotedVariationId) ?? version.variations[0];
}

/** the links that make other versions components of this one (a link into a quote has the quote's id as `to`) */
export function quoteStudioComponentLinks(links: readonly QuoteStudioLink[], versionId: string): QuoteStudioLink[] {
    return links.filter(link => link.to === versionId);
}

/* ================================================================= hardware rows */

function catalogPrice(catalog: QuoteStudioCatalogPrices, hardwareItemId: string | null): QuoteStudioCatalogPrice | null {
    if (!hardwareItemId) {
        return null;
    }
    if (catalog instanceof Map) {
        return catalog.get(hardwareItemId) ?? null;
    }
    return (catalog as Readonly<Record<string, QuoteStudioCatalogPrice>>)[hardwareItemId] ?? null;
}

export function quoteStudioCatalogPriceFor(catalog: QuoteStudioCatalogPrices, hardwareItemId: string | null): QuoteStudioCatalogPrice | null {
    return catalogPrice(catalog, hardwareItemId);
}

/** a cataloged row reads through to the catalog; a free-form row owns its values; a cataloged row missing from the catalog is $0 */
export function quoteStudioRowUnitCost(row: QuoteStudioHardwareRow, catalog: QuoteStudioCatalogPrices): number {
    const entry = catalogPrice(catalog, row.hardwareItemId);
    if (entry) {
        return entry.unitCost;
    }
    if (row.hardwareItemId) {
        return 0;
    }
    return Math.max(0, row.unitCost || 0);
}

/** how many parts one packaging container holds */
export function quoteStudioRowPer(row: QuoteStudioHardwareRow): number {
    return Math.max(1, row.per ?? 1);
}

/** packaging divides: one $18.50 gaylord across 24 parts is $0.77 a part */
export function quoteStudioPackagingPerPart(row: QuoteStudioHardwareRow, catalog: QuoteStudioCatalogPrices): number {
    return (row.quantity || 0) * quoteStudioRowUnitCost(row, catalog) / quoteStudioRowPer(row);
}

export function quoteStudioSumRows(rows: readonly QuoteStudioHardwareRow[] | undefined, catalog: QuoteStudioCatalogPrices): number {
    return (rows ?? []).reduce((total, row) => total + (row.quantity || 0) * quoteStudioRowUnitCost(row, catalog), 0);
}

export function quoteStudioSumPackaging(rows: readonly QuoteStudioHardwareRow[] | undefined, catalog: QuoteStudioCatalogPrices): number {
    return (rows ?? []).reduce((total, row) => total + quoteStudioPackagingPerPart(row, catalog), 0);
}

/* ================================================================= cost math */

function ownWeight(variation: QuoteStudioVariation): number {
    return variation.molded ? quoteStudioResins(variation).reduce((total, resin) => total + resin.pounds, 0) : 0;
}

function versionById(context: QuoteStudioCostingContext, id: string): QuoteStudioCostModelVersion | undefined {
    return context.versions.find(version => version.id === id);
}

/**
 * Cost of one variation of one version, at the rates in the context. Components
 * come from the links into this version, each of which already names the exact
 * child version, costed at that child's quoted variation. The visited set stops
 * a cycle from recursing forever; the studio refuses to create one, but the
 * math must not depend on that.
 */
export function calculateQuoteStudioVariationCost(
    context: QuoteStudioCostingContext,
    version: QuoteStudioCostModelVersion | null | undefined,
    variation: QuoteStudioVariation | null | undefined,
    visited: readonly string[] = [],
): QuoteStudioCostBreakdown {
    if (!version || !variation) {
        return { ...QUOTE_STUDIO_EMPTY_BREAKDOWN };
    }
    const { rates, catalog } = context;
    const molded = variation.molded;
    const material = molded ? quoteStudioResinCost(rates, variation) : 0;
    const machine = molded
        ? (variation.cycleMinutes || 0) * ((variation.armPercent || 0) / 100) * (quoteStudioMachineRate(rates, variation.machineKey) / 60)
        : 0;
    // a flat charge for the things moulding consumes that are not worth metering
    const moldingFixed = molded ? Math.max(0, rates.moldingFixedCostPerPart || 0) : 0;
    const moldedIn = molded ? quoteStudioSumRows(variation.moldedIn, catalog) : 0;
    const labor = quoteStudioActiveOpKeys(variation)
        .reduce((total, key) => total + (variation.ops[key] ?? 0) * quoteStudioOpRate(rates, key), 0);
    const secondary = quoteStudioSumRows(variation.secondary, catalog);
    const packaging = quoteStudioSumPackaging(variation.packaging, catalog);
    let linked = 0;
    for (const link of quoteStudioComponentLinks(context.links, version.id)) {
        const child = versionById(context, link.from);
        if (!child || visited.includes(child.id)) {
            continue;
        }
        linked += (link.quantity || 0)
            * calculateQuoteStudioVariationCost(context, child, quoteStudioQuotedVariation(child), [...visited, version.id]).total;
    }
    // The sheet's "Total Cost (including scrap & commission)": material & labor
    // x (1 + scrap + commission) - the two percentages added, not compounded.
    // Components are inside the base, so a scrapped assembly loses the parts in
    // it too - and each child already carries its own scrap in its total.
    const base = material + machine + moldingFixed + moldedIn + labor + secondary + packaging + linked;
    const scrap = base * (quoteStudioScrapPercent(variation) / 100);
    const commission = base * (quoteStudioCommissionPercent(variation) / 100);
    return {
        material, machine, moldingFixed, moldedIn, labor, secondary, packaging, linked, scrap, commission,
        total: base + scrap + commission,
    };
}

/** a version at its quoted variation — what its card shows */
export function calculateQuoteStudioVersionCost(
    context: QuoteStudioCostingContext,
    version: QuoteStudioCostModelVersion,
    visited: readonly string[] = [],
): QuoteStudioCostBreakdown {
    return calculateQuoteStudioVariationCost(context, version, quoteStudioQuotedVariation(version), visited);
}

/** resin pounds roll up through components, resolved the same way as cost */
export function calculateQuoteStudioShotWeight(
    context: QuoteStudioCostingContext,
    version: QuoteStudioCostModelVersion | null | undefined,
    variation: QuoteStudioVariation | null | undefined,
    visited: readonly string[] = [],
): number {
    if (!version || !variation) {
        return 0;
    }
    let total = ownWeight(variation);
    for (const link of quoteStudioComponentLinks(context.links, version.id)) {
        const child = versionById(context, link.from);
        if (!child || visited.includes(child.id)) {
            continue;
        }
        total += (link.quantity || 0)
            * calculateQuoteStudioShotWeight(context, child, quoteStudioQuotedVariation(child), [...visited, version.id]);
    }
    return total;
}

export function calculateQuoteStudioVersionShotWeight(
    context: QuoteStudioCostingContext,
    version: QuoteStudioCostModelVersion,
    visited: readonly string[] = [],
): number {
    return calculateQuoteStudioShotWeight(context, version, quoteStudioQuotedVariation(version), visited);
}

/** true when making `childId` a component of `parentId` would loop back on itself */
export function quoteStudioWouldCycle(
    links: readonly QuoteStudioLink[],
    childId: string,
    parentId: string,
    seen: readonly string[] = [],
): boolean {
    if (childId === parentId) {
        return true;
    }
    if (seen.includes(parentId)) {
        return false;
    }
    return links
        .filter(link => link.from === parentId)
        .some(link => quoteStudioWouldCycle(links, childId, link.to, [...seen, parentId]));
}

/* ================================================================= margin */

export function quoteStudioPriceFromMargin(cost: number, marginPercent: number): number {
    const divisor = 1 - marginPercent / 100;
    return divisor > 0 ? cost / divisor : 0;
}

export function quoteStudioMarginFromPrice(cost: number, price: number): number {
    return price > 0 ? (1 - cost / price) * 100 : 0;
}

/* ================================================================= catalog
   ONE ENTRY PER HARDWARE ITEM. Vendor hardware data exists so an item can be
   bought from several vendors - it is not a thing to quote against. The price
   comes from the item's default vendor when it has one that can be priced, and
   from its cheapest usable option when it does not. */

/** cost / packageQuantity, or 0 when there is no package quantity to divide by */
export function quoteStudioVendorUnitCost(option: VendorHardwareData): number {
    return option.packageQuantity > 0 ? (option.cost || 0) / option.packageQuantity : 0;
}

/**
 * A vendor row with no package quantity cannot produce a unit cost at all - it
 * is an unfilled placeholder, not a cheap option. Picking the "cheapest" without
 * this check handed those rows the win and priced real components at $0.
 */
export function quoteStudioVendorUsable(option: VendorHardwareData): boolean {
    return option.packageQuantity > 0;
}

/** the vendor row the studio prices an item from, or null when nothing prices it */
export function chooseQuoteStudioVendorOption(
    hardware: Pick<PlasticsHardwareItem, 'defaultVendorHardwareDataId'>,
    activeOptions: readonly VendorHardwareData[],
): VendorHardwareData | null {
    const options = activeOptions.filter(quoteStudioVendorUsable);
    const cheapest = [...options].sort((a, b) => quoteStudioVendorUnitCost(a) - quoteStudioVendorUnitCost(b));
    return options.find(option => option.id === hardware.defaultVendorHardwareDataId)
        ?? cheapest.find(option => quoteStudioVendorUnitCost(option) > 0)
        ?? cheapest[0]
        ?? null;
}

/** active vendor rows grouped by the hardware item they price */
export function groupQuoteStudioVendorOptions(vendorData: readonly VendorHardwareData[]): Map<string, VendorHardwareData[]> {
    const optionsByItem = new Map<string, VendorHardwareData[]>();
    for (const option of vendorData) {
        if (option.active === false) {
            continue;
        }
        const list = optionsByItem.get(option.parentHardwareItemId) ?? [];
        list.push(option);
        optionsByItem.set(option.parentHardwareItemId, list);
    }
    return optionsByItem;
}

/** the live price of every hardware item that has one; items nothing prices are left out */
export function buildQuoteStudioCatalogPrices(
    hardwareItems: readonly PlasticsHardwareItem[],
    vendorData: readonly VendorHardwareData[],
): Map<string, QuoteStudioCatalogPrice> {
    const optionsByItem = groupQuoteStudioVendorOptions(vendorData);
    const prices = new Map<string, QuoteStudioCatalogPrice>();
    for (const hardware of hardwareItems) {
        const preferred = chooseQuoteStudioVendorOption(hardware, optionsByItem.get(hardware.id) ?? []);
        if (!preferred) {
            continue;
        }
        prices.set(hardware.id, {
            hardwareItemId: hardware.id,
            partNumber: hardware.partNumber ?? '',
            description: hardware.description ?? '',
            units: String(hardware.units ?? preferred.units ?? 'ea'),
            unitCost: quoteStudioVendorUnitCost(preferred),
        });
    }
    return prices;
}
